//#region Imports
import { isMobile } from "../is_mobile";
//#endregion

//#region Constants

/**
 * CSS selector string for all focusable elements.
 * Used to identify elements that can receive keyboard focus.
 */
const FOCUSABLE_SELECTOR = [
  "a[href]",
  "area[href]",
  "button:not([disabled])",
  'input:not([disabled]):not([type="hidden"])',
  "select:not([disabled])",
  "textarea:not([disabled])",
  '[contenteditable="true"]',
  '[tabindex]:not([tabindex="-1"])',
].join(",");

//#endregion

//#region Focusable Elements Retrieval

/**
 * Retrieves all focusable elements within a given DOM root.
 * @param root - The DOM element to search within
 * @returns Array of focusable elements, empty if none found
 */
export function getFocusableElements(root: HTMLElement): HTMLElement[] {
  try {
    const all = Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR));
    return all.filter((el) => el.offsetParent !== null);
  } catch (err) {
    logError("getFocusableElements", err);
    return [];
  }
}

/**
 * Checks if the document body has an active modal class.
 * @returns True if body has active modal class, false otherwise
 */
function bodyHasActiveModal(): boolean {
  try {
    return document.body?.classList?.contains("satisfi_modal-show") ?? false;
  } catch {
    return false;
  }
}

/**
 * Handles Tab key navigation within a modal, cycling focus between focusable elements.
 * @param e - The keyboard event
 * @param modal - The modal element to manage focus within
 */
export function handleTabKey(e: KeyboardEvent, modal: HTMLElement) {
  try {
    if (e.key !== "Tab") return;
    if (!bodyHasActiveModal()) return;  
    let focusableElements = getFocusableElements(modal);

    if (focusableElements.length === 0) {
      e.preventDefault();
      (modal as HTMLElement).focus();
      return;
    }

    // Get the first and last focusable elements
    const first = focusableElements[0];
    const last = focusableElements[focusableElements.length - 1];
    const active = document.activeElement as HTMLElement | null;

    if (e.shiftKey) {
      if (active === first || !modal.contains(active)) {
        e.preventDefault();
        last.focus();
      }
    } else {
      if (active === last || !modal.contains(active)) {
        e.preventDefault();
        first.focus();
      }
    }
  } catch (err) {
    logError("handleTabKey", err);
  }
}

//#endregion

//#region Modal Focus Trap Public API

/**
 * Sets up focus trap for a modal element and returns a cleanup function.
 * @param modal - The modal element to trap focus within
 * @returns Cleanup function to remove the focus trap
 */
export function trapFocus(modal: HTMLElement): () => void {
  try {
    // Check for mobile first - if mobile, don't set up focus trap at all
    if (isMobile.any() || !modal) return () => { /* no cleanup */ };

    // Ensure modal is focusable host
    if (!modal.hasAttribute("tabindex")) modal.setAttribute("tabindex", "-1");

    // Move focus inside the modal
    modal.focus({ preventScroll: true });

    const keyHandler = (e: KeyboardEvent) => handleTabKey(e, modal);
    document.addEventListener("keydown", keyHandler, true);

		// Return cleanup function
    return () => {
      document.removeEventListener("keydown", keyHandler, true);
    };
  } catch (err) {
    logError("trapFocus", err);
    return () => { /* no cleanup */ };
  }
}

//#endregion

//#region Error Handling

/**
 * Logs an error with a consistent format and context.
 *
 * @param context - Function or operation name where error occurred
 * @param error - The error object or message
 */
function logError(context: string, error: any): void {
  console.error(`[focus_trap.ts] An error has occurred in ${context}:`, error);
}

//#endregion
